export type Language = 'en' | 'es' | 'fr' | 'de' | 'it' | 'zh' | 'ja' | 'ar' | 'pt' | 'ru';

export interface LanguageInfo {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

export const languages: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
];

export type TranslationKey = keyof typeof translations.en;

export const translations = {
  en: {
    // Navigation
    menu: 'Menu',
    cart: 'Cart',
    orders: 'Orders',
    settings: 'Settings',

    // Menu Categories
    allCategories: 'All Categories',
    appetizers: 'Appetizers',
    mainCourse: 'Main Course',
    desserts: 'Desserts',
    beverages: 'Beverages',

    // Menu Item Details
    spiceLevel: 'Spice Level',
    vegan: 'Vegan',
    vegetarian: 'Vegetarian',
    halal: 'Halal',
    allergens: 'Allergens',
    addToCart: 'Add to Cart',

    // Cart
    yourCart: 'Your Cart',
    emptyCart: 'Your cart is empty',
    subtotal: 'Subtotal',
    tax: 'Tax',
    total: 'Total',
    checkout: 'Checkout',
    remove: 'Remove',

    // Checkout
    paymentMethod: 'Payment Method',
    payOnline: 'Pay Online',
    payWithCash: 'Pay at the Counter (Pay Later)',
    card: 'Card',
    cash: 'Pay Later',
    customerNotes: 'Customer Notes',
    optional: 'Optional',
    placeOrder: 'Place Order',
    processing: 'Processing...',
    orderSuccess: 'Order placed successfully!',
    orderError: 'Failed to place order',

    // AI Waiter
    aiWaiter: 'AI Waiter',
    aiWaiterWelcome: "Welcome! I'm your AI Waiter. I can help you with menu recommendations, dietary restrictions, allergens, and answer any questions about our dishes. How can I help you today?",
    typeMessage: 'Type your message...',
    send: 'Send',
    voiceToggle: 'Toggle voice responses',
    voiceInput: 'Voice input',
    close: 'Close',
    tapToTalk: 'Tap to talk',
    listening: 'Listening...',
    aiProcessing: 'Processing...',
    speaking: 'Speaking...',

    // Restaurant Info
    address: 'Address',
    phone: 'Phone',
    hours: 'Hours',

    // Status
    available: 'Available',
    unavailable: 'Unavailable',
    loading: 'Loading...',
    error: 'Error',

    // Common
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',

    // Call Waiter
    callForAssistance: 'Call for Assistance',
    callWaiter: 'Call Waiter',
    requestSent: 'Request Sent!',
    someoneWillBeWithYouSoon: 'Someone will be with you soon',
    assistanceQuestion: 'Is there anything specific we can help you with? (Optional)',
    assistancePlaceholder: 'E.g., Need extra napkins, have a question about the menu, etc.',
    staffNotified: 'A staff member will be notified and come to your table shortly.',
    waiterCalled: 'Waiter Called',
    staffMemberWillBeWithYouShortly: 'A staff member will be with you shortly!',
    unableToContactRestaurant: 'Unable to contact restaurant. Please refresh the page.',

    // Receipt
    emailReceipt: 'Email Receipt',
    receipt: 'Receipt',
    invoice: 'Invoice',
    thankYouForYourOrder: 'Thank you for your order',
    orderNumber: 'Order Number',
    dateAndTime: 'Date & Time',
    table: 'Table',
    orderItems: 'Order Items',
    receiptSent: 'Receipt Sent!',
    receiptSentTo: 'Your receipt has been sent to',
    failedToSend: 'Failed to Send',
    couldNotSendEmail: 'Could not send email receipt. Please try again.',
    invalidEmail: 'Invalid Email',
    pleaseEnterValidEmail: 'Please enter a valid email address.',
    emailAddress: 'Email Address',
    sendReceipt: 'Send Receipt',
    sending: 'Sending...',
    printSavePDF: 'Print / Save PDF',
    printing: 'Printing...',
    trackYourOrder: 'Track Your Order',
    trackOrder: 'Track Order',
    continueBrowsingMenu: 'Continue Browsing Menu',
    specialInstructions: 'Special Instructions',
    paid: 'Paid',
    pending: 'Pending',
    paymentCompleted: 'Payment Completed',
    paymentPending: 'Payment Pending',
    receiptNotFound: 'Receipt not found',
    openingOrderTracking: 'Opening Order Tracking',
    orderTrackingWillOpen: 'Your order tracking page will open in a new tab',
    enterEmailToReceiveReceipt: 'Enter your email address to receive a copy of this receipt',

    // Rating & Feedback
    submitRating: 'Submit Rating',
    foodQuality: 'Food Quality',
    serviceSpeed: 'Service Speed',
    cleanliness: 'Cleanliness',
    staffFriendliness: 'Staff Friendliness',
    howWasThisDish: 'How was this dish?',
    overallExperience: 'Overall Experience',
    additionalFeedback: 'Any additional feedback?',
    rateYourFood: 'Rate Your Food',
    rateOurService: 'Rate Our Service',
    rateItem: 'Rate item',
    of: 'of',
    tellUsAboutYourExperience: 'Tell us about your overall experience',
    additionalComments: 'Additional Comments (Optional)',
    tellUsMoreAboutYourExperience: 'Tell us more about your experience...',
    previous: 'Previous',
    nextItem: 'Next Item',
    rateService: 'Rate Service',
    back: 'Back',
    thankYou: 'Thank you!',
    ratingSubmittedSuccessfully: 'Your rating has been submitted successfully.',

    // Welcome Overlay
    welcome: 'Welcome!',
    meetYourAIWaiter: 'Meet your AI waiter - ready to help you explore our menu and place your order',
    tapToStart: 'Tap to Start',
    tapAnywhereToContinue: 'Tap anywhere to continue',

    // Contact & Support
    contactSupport: 'Contact Support',
    questionsContact: 'Questions? Contact',
    directly: 'directly.',
    thankYouForDining: 'Thank you for dining with us!',
  },
  es: {
    // Navigation
    menu: 'Menú',
    cart: 'Carrito',
    orders: 'Pedidos',
    settings: 'Configuración',

    // Menu Categories
    allCategories: 'Todas las Categorías',
    appetizers: 'Aperitivos',
    mainCourse: 'Plato Principal',
    desserts: 'Postres',
    beverages: 'Bebidas',

    // Menu Item Details
    spiceLevel: 'Nivel de Picante',
    vegan: 'Vegano',
    vegetarian: 'Vegetariano',
    halal: 'Halal',
    allergens: 'Alérgenos',
    addToCart: 'Añadir al Carrito',

    // Cart
    yourCart: 'Tu Carrito',
    emptyCart: 'Tu carrito está vacío',
    subtotal: 'Subtotal',
    tax: 'Impuesto',
    total: 'Total',
    checkout: 'Pagar',
    remove: 'Eliminar',

    // Checkout
    paymentMethod: 'Método de Pago',
    payOnline: 'Pagar en Línea',
    payWithCash: 'Pagar en el Mostrador (Pagar Después)',
    card: 'Tarjeta',
    cash: 'Pagar Después',
    customerNotes: 'Notas del Cliente',
    optional: 'Opcional',
    placeOrder: 'Realizar Pedido',
    processing: 'Procesando...',
    orderSuccess: '¡Pedido realizado con éxito!',
    orderError: 'Error al realizar el pedido',

    // AI Waiter
    aiWaiter: 'Mesero IA',
    aiWaiterWelcome: '¡Bienvenido! Soy tu Mesero IA. Puedo ayudarte con recomendaciones del menú, restricciones dietéticas, alérgenos y responder cualquier pregunta sobre nuestros platos. ¿Cómo puedo ayudarte hoy?',
    typeMessage: 'Escribe tu mensaje...',
    send: 'Enviar',
    voiceToggle: 'Alternar respuestas de voz',
    voiceInput: 'Entrada de voz',
    close: 'Cerrar',
    tapToTalk: 'Toca para hablar',
    listening: 'Escuchando...',
    aiProcessing: 'Procesando...',
    speaking: 'Hablando...',

    // Restaurant Info
    address: 'Dirección',
    phone: 'Teléfono',
    hours: 'Horario',

    // Status
    available: 'Disponible',
    unavailable: 'No Disponible',
    loading: 'Cargando...',
    error: 'Error',

    // Common
    cancel: 'Cancelar',
    save: 'Guardar',
    delete: 'Eliminar',
    edit: 'Editar',
    confirm: 'Confirmar',
    yes: 'Sí',
    no: 'No',

    // Call Waiter
    callForAssistance: 'Llamar para Asistencia',
    callWaiter: 'Llamar al Mesero',
    requestSent: '¡Solicitud Enviada!',
    someoneWillBeWithYouSoon: 'Alguien estará contigo pronto',
    assistanceQuestion: '¿Hay algo específico en lo que podamos ayudarte? (Opcional)',
    assistancePlaceholder: 'Ej., Necesito servilletas extra, tengo una pregunta sobre el menú, etc.',
    staffNotified: 'Un miembro del personal será notificado y vendrá a tu mesa en breve.',
    waiterCalled: 'Mesero Llamado',
    staffMemberWillBeWithYouShortly: '¡Un miembro del personal estará contigo en breve!',
    unableToContactRestaurant: 'No se puede contactar con el restaurante. Por favor, actualice la página.',

    // Receipt
    emailReceipt: 'Enviar Recibo por Email',
    receipt: 'Recibo',
    invoice: 'Factura',
    thankYouForYourOrder: 'Gracias por tu pedido',
    orderNumber: 'Número de Pedido',
    dateAndTime: 'Fecha y Hora',
    table: 'Mesa',
    orderItems: 'Artículos del Pedido',
    receiptSent: '¡Recibo Enviado!',
    receiptSentTo: 'Tu recibo ha sido enviado a',
    failedToSend: 'Error al Enviar',
    couldNotSendEmail: 'No se pudo enviar el recibo por email. Por favor, inténtalo de nuevo.',
    invalidEmail: 'Email Inválido',
    pleaseEnterValidEmail: 'Por favor, introduce una dirección de email válida.',
    emailAddress: 'Dirección de Email',
    sendReceipt: 'Enviar Recibo',
    sending: 'Enviando...',
    printSavePDF: 'Imprimir / Guardar PDF',
    printing: 'Imprimiendo...',
    trackYourOrder: 'Seguir Tu Pedido',
    trackOrder: 'Seguir Pedido',
    continueBrowsingMenu: 'Continuar Explorando el Menú',
    specialInstructions: 'Instrucciones Especiales',
    paid: 'Pagado',
    pending: 'Pendiente',
    paymentCompleted: 'Pago Completado',
    paymentPending: 'Pago Pendiente',
    receiptNotFound: 'Recibo no encontrado',
    openingOrderTracking: 'Abriendo Seguimiento del Pedido',
    orderTrackingWillOpen: 'Tu página de seguimiento del pedido se abrirá en una nueva pestaña',
    enterEmailToReceiveReceipt: 'Introduce tu dirección de email para recibir una copia de este recibo',

    // Rating & Feedback
    submitRating: 'Enviar Calificación',
    foodQuality: 'Calidad de la Comida',
    serviceSpeed: 'Rapidez del Servicio',
    cleanliness: 'Limpieza',
    staffFriendliness: 'Amabilidad del Personal',
    howWasThisDish: '¿Cómo estuvo este plato?',
    overallExperience: 'Experiencia General',
    additionalFeedback: '¿Algún comentario adicional?',
    rateYourFood: 'Califica Tu Comida',
    rateOurService: 'Califica Nuestro Servicio',
    rateItem: 'Calificar artículo',
    of: 'de',
    tellUsAboutYourExperience: 'Cuéntanos sobre tu experiencia general',
    additionalComments: 'Comentarios Adicionales (Opcional)',
    tellUsMoreAboutYourExperience: 'Cuéntanos más sobre tu experiencia...',
    previous: 'Anterior',
    nextItem: 'Siguiente Artículo',
    rateService: 'Calificar Servicio',
    back: 'Atrás',
    thankYou: '¡Gracias!',
    ratingSubmittedSuccessfully: 'Tu calificación ha sido enviada exitosamente.',

    // Welcome Overlay
    welcome: '¡Bienvenido!',
    meetYourAIWaiter: 'Conoce a tu mesero IA - listo para ayudarte a explorar nuestro menú y realizar tu pedido',
    tapToStart: 'Toca para Comenzar',
    tapAnywhereToContinue: 'Toca en cualquier lugar para continuar',

    // Contact & Support
    contactSupport: 'Contactar Soporte',
    questionsContact: '¿Preguntas? Contacta a',
    directly: 'directamente.',
    thankYouForDining: '¡Gracias por cenar con nosotros!',
  },
  fr: {
    // Navigation
    menu: 'Menu',
    cart: 'Panier',
    orders: 'Commandes',
    settings: 'Paramètres',

    // Menu Categories
    allCategories: 'Toutes les Catégories',
    appetizers: 'Entrées',
    mainCourse: 'Plat Principal',
    desserts: 'Desserts',
    beverages: 'Boissons',

    // Menu Item Details
    spiceLevel: 'Niveau de Piquant',
    vegan: 'Végétalien',
    vegetarian: 'Végétarien',
    halal: 'Halal',
    allergens: 'Allergènes',
    addToCart: 'Ajouter au Panier',

    // Cart
    yourCart: 'Votre Panier',
    emptyCart: 'Votre panier est vide',
    subtotal: 'Sous-total',
    tax: 'Taxe',
    total: 'Total',
    checkout: 'Commander',
    remove: 'Supprimer',

    // Checkout
    paymentMethod: 'Méthode de Paiement',
    payOnline: 'Payer en Ligne',
    payWithCash: 'Payer au Comptoir (Payer Plus Tard)',
    card: 'Carte',
    cash: 'Payer Plus Tard',
    customerNotes: 'Notes du Client',
    optional: 'Optionnel',
    placeOrder: 'Passer la Commande',
    processing: 'Traitement...',
    orderSuccess: 'Commande passée avec succès!',
    orderError: 'Échec de la commande',

    // AI Waiter
    aiWaiter: 'Serveur IA',
    aiWaiterWelcome: 'Bienvenue! Je suis votre Serveur IA. Je peux vous aider avec des recommandations de menu, des restrictions alimentaires, des allergènes et répondre à toutes vos questions sur nos plats. Comment puis-je vous aider aujourd\'hui?',
    typeMessage: 'Tapez votre message...',
    send: 'Envoyer',
    voiceToggle: 'Basculer les réponses vocales',
    voiceInput: 'Entrée vocale',
    close: 'Fermer',
    tapToTalk: 'Appuyer pour parler',
    listening: 'Écoute...',
    aiProcessing: 'Traitement...',
    speaking: 'Parle...',

    // Restaurant Info
    address: 'Adresse',
    phone: 'Téléphone',
    hours: 'Horaires',

    // Status
    available: 'Disponible',
    unavailable: 'Indisponible',
    loading: 'Chargement...',
    error: 'Erreur',

    // Common
    cancel: 'Annuler',
    save: 'Enregistrer',
    delete: 'Supprimer',
    edit: 'Modifier',
    confirm: 'Confirmer',
    yes: 'Oui',
    no: 'Non',

    // Call Waiter
    callForAssistance: 'Appeler pour Assistance',
    callWaiter: 'Appeler le Serveur',
    requestSent: 'Demande Envoyée!',
    someoneWillBeWithYouSoon: 'Quelqu\'un sera avec vous bientôt',
    assistanceQuestion: 'Y a-t-il quelque chose de spécifique pour lequel nous pouvons vous aider? (Facultatif)',
    assistancePlaceholder: 'Ex., Besoin de serviettes supplémentaires, une question sur le menu, etc.',
    staffNotified: 'Un membre du personnel sera notifié et viendra à votre table sous peu.',
    waiterCalled: 'Serveur Appelé',
    staffMemberWillBeWithYouShortly: 'Un membre du personnel sera avec vous sous peu!',
    unableToContactRestaurant: 'Impossible de contacter le restaurant. Veuillez actualiser la page.',

    // Receipt
    emailReceipt: 'Envoyer le Reçu par Email',
    receipt: 'Reçu',
    invoice: 'Facture',
    thankYouForYourOrder: 'Merci pour votre commande',
    orderNumber: 'Numéro de Commande',
    dateAndTime: 'Date et Heure',
    table: 'Table',
    orderItems: 'Articles de la Commande',
    receiptSent: 'Reçu Envoyé!',
    receiptSentTo: 'Votre reçu a été envoyé à',
    failedToSend: 'Échec de l\'Envoi',
    couldNotSendEmail: 'Impossible d\'envoyer le reçu par email. Veuillez réessayer.',
    invalidEmail: 'Email Invalide',
    pleaseEnterValidEmail: 'Veuillez entrer une adresse email valide.',
    emailAddress: 'Adresse Email',
    sendReceipt: 'Envoyer le Reçu',
    sending: 'Envoi...',
    printSavePDF: 'Imprimer / Sauvegarder PDF',
    printing: 'Impression...',
    trackYourOrder: 'Suivre Votre Commande',
    trackOrder: 'Suivre la Commande',
    continueBrowsingMenu: 'Continuer à Parcourir le Menu',
    specialInstructions: 'Instructions Spéciales',
    paid: 'Payé',
    pending: 'En Attente',
    paymentCompleted: 'Paiement Complété',
    paymentPending: 'Paiement en Attente',
    receiptNotFound: 'Reçu non trouvé',
    openingOrderTracking: 'Ouverture du Suivi de Commande',
    orderTrackingWillOpen: 'Votre page de suivi de commande s\'ouvrira dans un nouvel onglet',
    enterEmailToReceiveReceipt: 'Entrez votre adresse email pour recevoir une copie de ce reçu',

    // Rating & Feedback
    submitRating: 'Soumettre l\'Évaluation',
    foodQuality: 'Qualité de la Nourriture',
    serviceSpeed: 'Rapidité du Service',
    cleanliness: 'Propreté',
    staffFriendliness: 'Amabilité du Personnel',
    howWasThisDish: 'Comment était ce plat?',
    overallExperience: 'Expérience Globale',
    additionalFeedback: 'Des commentaires supplémentaires?',
    rateYourFood: 'Évaluez Votre Nourriture',
    rateOurService: 'Évaluez Notre Service',
    rateItem: 'Évaluer l\'article',
    of: 'de',
    tellUsAboutYourExperience: 'Parlez-nous de votre expérience globale',
    additionalComments: 'Commentaires Supplémentaires (Facultatif)',
    tellUsMoreAboutYourExperience: 'Parlez-nous davantage de votre expérience...',
    previous: 'Précédent',
    nextItem: 'Article Suivant',
    rateService: 'Évaluer le Service',
    back: 'Retour',
    thankYou: 'Merci!',
    ratingSubmittedSuccessfully: 'Votre évaluation a été soumise avec succès.',

    // Welcome Overlay
    welcome: 'Bienvenue!',
    meetYourAIWaiter: 'Rencontrez votre serveur IA - prêt à vous aider à explorer notre menu et passer votre commande',
    tapToStart: 'Appuyez pour Commencer',
    tapAnywhereToContinue: 'Appuyez n\'importe où pour continuer',

    // Contact & Support
    contactSupport: 'Contacter le Support',
    questionsContact: 'Questions? Contactez',
    directly: 'directement.',
    thankYouForDining: 'Merci d\'avoir dîné avec nous!',
  },
  de: {
    // Navigation
    menu: 'Menü',
    cart: 'Warenkorb',
    orders: 'Bestellungen',
    settings: 'Einstellungen',

    // Menu Categories
    allCategories: 'Alle Kategorien',
    appetizers: 'Vorspeisen',
    mainCourse: 'Hauptgericht',
    desserts: 'Desserts',
    beverages: 'Getränke',

    // Menu Item Details
    spiceLevel: 'Schärfegrad',
    vegan: 'Vegan',
    vegetarian: 'Vegetarisch',
    halal: 'Halal',
    allergens: 'Allergene',
    addToCart: 'In den Warenkorb',

    // Cart
    yourCart: 'Ihr Warenkorb',
    emptyCart: 'Ihr Warenkorb ist leer',
    subtotal: 'Zwischensumme',
    tax: 'Steuer',
    total: 'Gesamt',
    checkout: 'Zur Kasse',
    remove: 'Entfernen',

    // Checkout
    paymentMethod: 'Zahlungsmethode',
    payOnline: 'Online Bezahlen',
    payWithCash: 'Am Tresen Bezahlen (Später Bezahlen)',
    card: 'Karte',
    cash: 'Später Bezahlen',
    customerNotes: 'Kundennotizen',
    optional: 'Optional',
    placeOrder: 'Bestellung Aufgeben',
    processing: 'Verarbeitung...',
    orderSuccess: 'Bestellung erfolgreich aufgegeben!',
    orderError: 'Bestellung fehlgeschlagen',

    // AI Waiter
    aiWaiter: 'KI Kellner',
    aiWaiterWelcome: 'Willkommen! Ich bin Ihr KI-Kellner. Ich kann Ihnen bei Menüempfehlungen, Ernährungseinschränkungen, Allergenen helfen und alle Fragen zu unseren Gerichten beantworten. Wie kann ich Ihnen heute helfen?',
    typeMessage: 'Geben Sie Ihre Nachricht ein...',
    send: 'Senden',
    voiceToggle: 'Sprachantworten umschalten',
    voiceInput: 'Spracheingabe',
    close: 'Schließen',
    tapToTalk: 'Tippen zum Sprechen',
    listening: 'Hört zu...',
    aiProcessing: 'Verarbeitung...',
    speaking: 'Spricht...',

    // Restaurant Info
    address: 'Adresse',
    phone: 'Telefon',
    hours: 'Öffnungszeiten',

    // Status
    available: 'Verfügbar',
    unavailable: 'Nicht Verfügbar',
    loading: 'Laden...',
    error: 'Fehler',

    // Common
    cancel: 'Abbrechen',
    save: 'Speichern',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    confirm: 'Bestätigen',
    yes: 'Ja',
    no: 'Nein',

    // Call Waiter
    callForAssistance: 'Rufen Sie um Hilfe',
    callWaiter: 'Kellner Rufen',
    requestSent: 'Anfrage Gesendet!',
    someoneWillBeWithYouSoon: 'Jemand wird bald bei Ihnen sein',
    assistanceQuestion: 'Gibt es etwas Bestimmtes, bei dem wir Ihnen helfen können? (Optional)',
    assistancePlaceholder: 'Z.B., Brauche extra Servietten, habe eine Frage zum Menü, etc.',
    staffNotified: 'Ein Mitarbeiter wird benachrichtigt und kommt in Kürze zu Ihrem Tisch.',
    waiterCalled: 'Kellner Gerufen',
    staffMemberWillBeWithYouShortly: 'Ein Mitarbeiter wird in Kürze bei Ihnen sein!',
    unableToContactRestaurant: 'Restaurant kann nicht erreicht werden. Bitte aktualisieren Sie die Seite.',

    // Receipt
    emailReceipt: 'Quittung per E-Mail Senden',
    receipt: 'Quittung',
    invoice: 'Rechnung',
    thankYouForYourOrder: 'Vielen Dank für Ihre Bestellung',
    orderNumber: 'Bestellnummer',
    dateAndTime: 'Datum und Uhrzeit',
    table: 'Tisch',
    orderItems: 'Bestellartikel',
    receiptSent: 'Quittung Gesendet!',
    receiptSentTo: 'Ihre Quittung wurde gesendet an',
    failedToSend: 'Senden Fehlgeschlagen',
    couldNotSendEmail: 'Quittung konnte nicht per E-Mail gesendet werden. Bitte versuchen Sie es erneut.',
    invalidEmail: 'Ungültige E-Mail',
    pleaseEnterValidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    emailAddress: 'E-Mail-Adresse',
    sendReceipt: 'Quittung Senden',
    sending: 'Senden...',
    printSavePDF: 'Drucken / PDF Speichern',
    printing: 'Drucken...',
    trackYourOrder: 'Ihre Bestellung Verfolgen',
    trackOrder: 'Bestellung Verfolgen',
    continueBrowsingMenu: 'Weiter Menü Durchsuchen',
    specialInstructions: 'Besondere Anweisungen',
    paid: 'Bezahlt',
    pending: 'Ausstehend',
    paymentCompleted: 'Zahlung Abgeschlossen',
    paymentPending: 'Zahlung Ausstehend',
    receiptNotFound: 'Quittung nicht gefunden',
    openingOrderTracking: 'Bestellverfolgung Öffnen',
    orderTrackingWillOpen: 'Ihre Bestellverfolgungsseite wird in einem neuen Tab geöffnet',
    enterEmailToReceiveReceipt: 'Geben Sie Ihre E-Mail-Adresse ein, um eine Kopie dieser Quittung zu erhalten',

    // Rating & Feedback
    submitRating: 'Bewertung Absenden',
    foodQuality: 'Qualität des Essens',
    serviceSpeed: 'Servicegeschwindigkeit',
    cleanliness: 'Sauberkeit',
    staffFriendliness: 'Freundlichkeit des Personals',
    howWasThisDish: 'Wie war dieses Gericht?',
    overallExperience: 'Gesamterlebnis',
    additionalFeedback: 'Zusätzliches Feedback?',
    rateYourFood: 'Bewerten Sie Ihr Essen',
    rateOurService: 'Bewerten Sie Unseren Service',
    rateItem: 'Artikel bewerten',
    of: 'von',
    tellUsAboutYourExperience: 'Erzählen Sie uns von Ihrem Gesamterlebnis',
    additionalComments: 'Zusätzliche Kommentare (Optional)',
    tellUsMoreAboutYourExperience: 'Erzählen Sie uns mehr über Ihr Erlebnis...',
    previous: 'Vorherige',
    nextItem: 'Nächster Artikel',
    rateService: 'Service Bewerten',
    back: 'Zurück',
    thankYou: 'Vielen Dank!',
    ratingSubmittedSuccessfully: 'Ihre Bewertung wurde erfolgreich eingereicht.',

    // Welcome Overlay
    welcome: 'Willkommen!',
    meetYourAIWaiter: 'Treffen Sie Ihren KI-Kellner - bereit, Ihnen bei der Erkundung unserer Speisekarte und Bestellung zu helfen',
    tapToStart: 'Tippen zum Starten',
    tapAnywhereToContinue: 'Tippen Sie überall, um fortzufahren',

    // Contact & Support
    contactSupport: 'Support Kontaktieren',
    questionsContact: 'Fragen? Kontaktieren Sie',
    directly: 'direkt.',
    thankYouForDining: 'Vielen Dank fürs Speisen bei uns!',
  },
  it: {
    // Navigation
    menu: 'Menu',
    cart: 'Carrello',
    orders: 'Ordini',
    settings: 'Impostazioni',

    // Menu Categories
    allCategories: 'Tutte le Categorie',
    appetizers: 'Antipasti',
    mainCourse: 'Piatto Principale',
    desserts: 'Dolci',
    beverages: 'Bevande',

    // Menu Item Details
    spiceLevel: 'Livello di Piccante',
    vegan: 'Vegano',
    vegetarian: 'Vegetariano',
    halal: 'Halal',
    allergens: 'Allergeni',
    addToCart: 'Aggiungi al Carrello',

    // Cart
    yourCart: 'Il Tuo Carrello',
    emptyCart: 'Il tuo carrello è vuoto',
    subtotal: 'Subtotale',
    tax: 'Tassa',
    total: 'Totale',
    checkout: 'Procedi al Pagamento',
    remove: 'Rimuovi',

    // Checkout
    paymentMethod: 'Metodo di Pagamento',
    payOnline: 'Paga Online',
    payWithCash: 'Paga al Bancone (Paga Dopo)',
    card: 'Carta',
    cash: 'Paga Dopo',
    customerNotes: 'Note del Cliente',
    optional: 'Opzionale',
    placeOrder: 'Effettua Ordine',
    processing: 'Elaborazione...',
    orderSuccess: 'Ordine effettuato con successo!',
    orderError: 'Errore nell\'effettuare l\'ordine',

    // AI Waiter
    aiWaiter: 'Cameriere IA',
    aiWaiterWelcome: 'Benvenuto! Sono il tuo Cameriere IA. Posso aiutarti con raccomandazioni del menu, restrizioni dietetiche, allergeni e rispondere a qualsiasi domanda sui nostri piatti. Come posso aiutarti oggi?',
    typeMessage: 'Scrivi il tuo messaggio...',
    send: 'Invia',
    voiceToggle: 'Attiva/disattiva risposte vocali',
    voiceInput: 'Input vocale',
    close: 'Chiudi',
    tapToTalk: 'Tocca per parlare',
    listening: 'In ascolto...',
    aiProcessing: 'Elaborazione...',
    speaking: 'Parlando...',

    // Restaurant Info
    address: 'Indirizzo',
    phone: 'Telefono',
    hours: 'Orari',

    // Status
    available: 'Disponibile',
    unavailable: 'Non Disponibile',
    loading: 'Caricamento...',
    error: 'Errore',

    // Common
    cancel: 'Annulla',
    save: 'Salva',
    delete: 'Elimina',
    edit: 'Modifica',
    confirm: 'Conferma',
    yes: 'Sì',
    no: 'No',

    // Call Waiter
    callForAssistance: 'Chiama per Assistenza',
    callWaiter: 'Chiama il Cameriere',
    requestSent: 'Richiesta Inviata!',
    someoneWillBeWithYouSoon: 'Qualcuno sarà con te presto',
    assistanceQuestion: 'C\'è qualcosa di specifico per cui possiamo aiutarti? (Facoltativo)',
    assistancePlaceholder: 'Es., Ho bisogno di tovaglioli extra, ho una domanda sul menu, ecc.',
    staffNotified: 'Un membro dello staff sarà notificato e verrà al tuo tavolo a breve.',
    waiterCalled: 'Cameriere Chiamato',
    staffMemberWillBeWithYouShortly: 'Un membro dello staff sarà con te a breve!',
    unableToContactRestaurant: 'Impossibile contattare il ristorante. Si prega di aggiornare la pagina.',

    // Receipt
    emailReceipt: 'Invia Ricevuta via Email',
    receipt: 'Ricevuta',
    invoice: 'Fattura',
    thankYouForYourOrder: 'Grazie per il tuo ordine',
    orderNumber: 'Numero Ordine',
    dateAndTime: 'Data e Ora',
    table: 'Tavolo',
    orderItems: 'Articoli dell\'Ordine',
    receiptSent: 'Ricevuta Inviata!',
    receiptSentTo: 'La tua ricevuta è stata inviata a',
    failedToSend: 'Invio Fallito',
    couldNotSendEmail: 'Impossibile inviare la ricevuta via email. Riprova.',
    invalidEmail: 'Email Non Valida',
    pleaseEnterValidEmail: 'Inserisci un indirizzo email valido.',
    emailAddress: 'Indirizzo Email',
    sendReceipt: 'Invia Ricevuta',
    sending: 'Invio...',
    printSavePDF: 'Stampa / Salva PDF',
    printing: 'Stampa...',
    trackYourOrder: 'Traccia il Tuo Ordine',
    trackOrder: 'Traccia Ordine',
    continueBrowsingMenu: 'Continua a Sfogliare il Menu',
    specialInstructions: 'Istruzioni Speciali',
    paid: 'Pagato',
    pending: 'In Attesa',
    paymentCompleted: 'Pagamento Completato',
    paymentPending: 'Pagamento in Attesa',
    receiptNotFound: 'Ricevuta non trovata',
    openingOrderTracking: 'Apertura Tracciamento Ordine',
    orderTrackingWillOpen: 'La tua pagina di tracciamento ordine si aprirà in una nuova scheda',
    enterEmailToReceiveReceipt: 'Inserisci il tuo indirizzo email per ricevere una copia di questa ricevuta',

    // Rating & Feedback
    submitRating: 'Invia Valutazione',
    foodQuality: 'Qualità del Cibo',
    serviceSpeed: 'Velocità del Servizio',
    cleanliness: 'Pulizia',
    staffFriendliness: 'Cordialità del Personale',
    howWasThisDish: 'Come era questo piatto?',
    overallExperience: 'Esperienza Complessiva',
    additionalFeedback: 'Feedback aggiuntivo?',
    rateYourFood: 'Valuta il Tuo Cibo',
    rateOurService: 'Valuta il Nostro Servizio',
    rateItem: 'Valuta articolo',
    of: 'di',
    tellUsAboutYourExperience: 'Raccontaci della tua esperienza complessiva',
    additionalComments: 'Commenti Aggiuntivi (Facoltativo)',
    tellUsMoreAboutYourExperience: 'Raccontaci di più sulla tua esperienza...',
    previous: 'Precedente',
    nextItem: 'Articolo Successivo',
    rateService: 'Valuta il Servizio',
    back: 'Indietro',
    thankYou: 'Grazie!',
    ratingSubmittedSuccessfully: 'La tua valutazione è stata inviata con successo.',

    // Welcome Overlay
    welcome: 'Benvenuto!',
    meetYourAIWaiter: 'Incontra il tuo cameriere IA - pronto ad aiutarti a esplorare il nostro menu e effettuare il tuo ordine',
    tapToStart: 'Tocca per Iniziare',
    tapAnywhereToContinue: 'Tocca ovunque per continuare',

    // Contact & Support
    contactSupport: 'Contatta il Supporto',
    questionsContact: 'Domande? Contatta',
    directly: 'direttamente.',
    thankYouForDining: 'Grazie per aver cenato con noi!',
  },
  zh: {
    // Navigation
    menu: '菜单',
    cart: '购物车',
    orders: '订单',
    settings: '设置',

    // Menu Categories
    allCategories: '所有类别',
    appetizers: '开胃菜',
    mainCourse: '主菜',
    desserts: '甜点',
    beverages: '饮料',

    // Menu Item Details
    spiceLevel: '辣度',
    vegan: '纯素',
    vegetarian: '素食',
    halal: '清真',
    allergens: '过敏源',
    addToCart: '加入购物车',

    // Cart
    yourCart: '您的购物车',
    emptyCart: '购物车为空',
    subtotal: '小计',
    tax: '税',
    total: '总计',
    checkout: '结账',
    remove: '移除',

    // Checkout
    paymentMethod: '支付方式',
    payOnline: '在线支付',
    payWithCash: '在柜台付款（稍后付款）',
    card: '卡',
    cash: '稍后付款',
    customerNotes: '客户备注',
    optional: '可选',
    placeOrder: '下订单',
    processing: '处理中...',
    orderSuccess: '订单成功！',
    orderError: '订单失败',

    // AI Waiter
    aiWaiter: 'AI 服务员',
    aiWaiterWelcome: '欢迎！我是您的AI服务员。我可以帮助您了解菜单推荐、饮食限制、过敏源，并回答有关我们菜品的任何问题。今天我能为您做些什么？',
    typeMessage: '输入您的消息...',
    send: '发送',
    voiceToggle: '切换语音响应',
    voiceInput: '语音输入',
    close: '关闭',
    tapToTalk: '点击说话',
    listening: '正在聆听...',
    aiProcessing: '处理中...',
    speaking: '正在说话...',

    // Restaurant Info
    address: '地址',
    phone: '电话',
    hours: '营业时间',

    // Status
    available: '可用',
    unavailable: '不可用',
    loading: '加载中...',
    error: '错误',

    // Common
    cancel: '取消',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    confirm: '确认',
    yes: '是',
    no: '否',

    // Call Waiter
    callForAssistance: '请求协助',
    callWaiter: '呼叫服务员',
    requestSent: '请求已发送！',
    someoneWillBeWithYouSoon: '工作人员将很快为您服务',
    assistanceQuestion: '有什么我们可以帮助您的吗？（可选）',
    assistancePlaceholder: '例如，需要额外的餐巾纸，有关于菜单的问题等',
    staffNotified: '工作人员将收到通知并很快来到您的桌旁。',
    waiterCalled: '已呼叫服务员',
    staffMemberWillBeWithYouShortly: '工作人员将很快为您服务！',
    unableToContactRestaurant: '无法联系餐厅。请刷新页面。',

    // Receipt
    emailReceipt: '发送收据到邮箱',
    receipt: '收据',
    invoice: '发票',
    thankYouForYourOrder: '感谢您的订单',
    orderNumber: '订单号',
    dateAndTime: '日期和时间',
    table: '桌号',
    orderItems: '订单项目',
    receiptSent: '收据已发送！',
    receiptSentTo: '您的收据已发送至',
    failedToSend: '发送失败',
    couldNotSendEmail: '无法通过电子邮件发送收据。请重试。',
    invalidEmail: '无效的电子邮件',
    pleaseEnterValidEmail: '请输入有效的电子邮件地址。',
    emailAddress: '电子邮件地址',
    sendReceipt: '发送收据',
    sending: '发送中...',
    printSavePDF: '打印 / 保存 PDF',
    printing: '打印中...',
    trackYourOrder: '追踪您的订单',
    trackOrder: '追踪订单',
    continueBrowsingMenu: '继续浏览菜单',
    specialInstructions: '特殊说明',
    paid: '已付款',
    pending: '待处理',
    paymentCompleted: '支付完成',
    paymentPending: '支付待处理',
    receiptNotFound: '未找到收据',
    openingOrderTracking: '打开订单追踪',
    orderTrackingWillOpen: '您的订单追踪页面将在新标签页中打开',
    enterEmailToReceiveReceipt: '输入您的电子邮件地址以接收此收据的副本',

    // Rating & Feedback
    submitRating: '提交评价',
    foodQuality: '食物质量',
    serviceSpeed: '服务速度',
    cleanliness: '清洁度',
    staffFriendliness: '员工友好度',
    howWasThisDish: '这道菜怎么样？',
    overallExperience: '总体体验',
    additionalFeedback: '还有其他反馈吗？',
    rateYourFood: '评价您的食物',
    rateOurService: '评价我们的服务',
    rateItem: '评价项目',
    of: '的',
    tellUsAboutYourExperience: '告诉我们您的总体体验',
    additionalComments: '其他评论（可选）',
    tellUsMoreAboutYourExperience: '告诉我们更多关于您的体验...',
    previous: '上一个',
    nextItem: '下一项',
    rateService: '评价服务',
    back: '返回',
    thankYou: '谢谢！',
    ratingSubmittedSuccessfully: '您的评价已成功提交。',

    // Welcome Overlay
    welcome: '欢迎！',
    meetYourAIWaiter: '遇见您的AI服务员 - 随时准备帮助您浏览菜单并下订单',
    tapToStart: '点击开始',
    tapAnywhereToContinue: '点击任何位置继续',

    // Contact & Support
    contactSupport: '联系支持',
    questionsContact: '有问题？联系',
    directly: '直接联系。',
    thankYouForDining: '感谢您的光临！',
  },
  ja: {
    // Navigation
    menu: 'メニュー',
    cart: 'カート',
    orders: '注文',
    settings: '設定',

    // Menu Categories
    allCategories: 'すべてのカテゴリ',
    appetizers: '前菜',
    mainCourse: 'メインコース',
    desserts: 'デザート',
    beverages: '飲み物',

    // Menu Item Details
    spiceLevel: '辛さレベル',
    vegan: 'ヴィーガン',
    vegetarian: 'ベジタリアン',
    halal: 'ハラール',
    allergens: 'アレルゲン',
    addToCart: 'カートに追加',

    // Cart
    yourCart: 'あなたのカート',
    emptyCart: 'カートは空です',
    subtotal: '小計',
    tax: '税',
    total: '合計',
    checkout: 'チェックアウト',
    remove: '削除',

    // Checkout
    paymentMethod: '支払い方法',
    payOnline: 'オンライン決済',
    payWithCash: 'カウンターでお支払い（後払い）',
    card: 'カード',
    cash: '後払い',
    customerNotes: '顧客メモ',
    optional: 'オプション',
    placeOrder: '注文する',
    processing: '処理中...',
    orderSuccess: '注文が成功しました！',
    orderError: '注文に失敗しました',

    // AI Waiter
    aiWaiter: 'AIウェイター',
    aiWaiterWelcome: 'ようこそ！私はあなたのAIウェイターです。メニューの推奨、食事制限、アレルゲンについてお手伝いし、料理に関するご質問にお答えします。今日はどのようにお手伝いできますか？',
    typeMessage: 'メッセージを入力...',
    send: '送信',
    voiceToggle: '音声応答の切り替え',
    voiceInput: '音声入力',
    close: '閉じる',
    tapToTalk: 'タップして話す',
    listening: '聞いています...',
    aiProcessing: '処理中...',
    speaking: '話しています...',

    // Restaurant Info
    address: '住所',
    phone: '電話',
    hours: '営業時間',

    // Status
    available: '利用可能',
    unavailable: '利用不可',
    loading: '読み込み中...',
    error: 'エラー',

    // Common
    cancel: 'キャンセル',
    save: '保存',
    delete: '削除',
    edit: '編集',
    confirm: '確認',
    yes: 'はい',
    no: 'いいえ',

    // Call Waiter
    callForAssistance: 'サポートを呼ぶ',
    callWaiter: 'ウェイターを呼ぶ',
    requestSent: 'リクエストを送信しました！',
    someoneWillBeWithYouSoon: 'すぐにスタッフがお伺いします',
    assistanceQuestion: '何か特別にお手伝いできることはありますか？（オプション）',
    assistancePlaceholder: '例：ナプキンが必要、メニューについて質問があるなど',
    staffNotified: 'スタッフに通知され、まもなくお客様のテーブルにお伺いします。',
    waiterCalled: 'ウェイターを呼びました',
    staffMemberWillBeWithYouShortly: 'まもなくスタッフがお伺いします！',
    unableToContactRestaurant: 'レストランに連絡できません。ページを更新してください。',

    // Receipt
    emailReceipt: 'レシートをメールで送信',
    receipt: 'レシート',
    invoice: '請求書',
    thankYouForYourOrder: 'ご注文ありがとうございます',
    orderNumber: '注文番号',
    dateAndTime: '日付と時刻',
    table: 'テーブル',
    orderItems: '注文商品',
    receiptSent: 'レシート送信完了！',
    receiptSentTo: 'レシートを送信しました',
    failedToSend: '送信失敗',
    couldNotSendEmail: 'メールでレシートを送信できませんでした。もう一度お試しください。',
    invalidEmail: '無効なメール',
    pleaseEnterValidEmail: '有効なメールアドレスを入力してください。',
    emailAddress: 'メールアドレス',
    sendReceipt: 'レシートを送信',
    sending: '送信中...',
    printSavePDF: '印刷 / PDFを保存',
    printing: '印刷中...',
    trackYourOrder: '注文を追跡',
    trackOrder: '注文追跡',
    continueBrowsingMenu: 'メニューを続ける',
    specialInstructions: '特別な指示',
    paid: '支払い済み',
    pending: '保留中',
    paymentCompleted: '支払い完了',
    paymentPending: '支払い保留中',
    receiptNotFound: 'レシートが見つかりません',
    openingOrderTracking: '注文追跡を開く',
    orderTrackingWillOpen: '注文追跡ページが新しいタブで開きます',
    enterEmailToReceiveReceipt: 'このレシートのコピーを受け取るためにメールアドレスを入力してください',

    // Rating & Feedback
    submitRating: '評価を送信',
    foodQuality: '料理の質',
    serviceSpeed: 'サービスの速さ',
    cleanliness: '清潔さ',
    staffFriendliness: 'スタッフの親切さ',
    howWasThisDish: 'この料理はいかがでしたか？',
    overallExperience: '全体的な体験',
    additionalFeedback: '追加のフィードバックはありますか？',
    rateYourFood: '料理を評価',
    rateOurService: 'サービスを評価',
    rateItem: '項目を評価',
    of: 'の',
    tellUsAboutYourExperience: '全体的な体験について教えてください',
    additionalComments: '追加のコメント（オプション）',
    tellUsMoreAboutYourExperience: '体験についてさらに教えてください...',
    previous: '前へ',
    nextItem: '次の項目',
    rateService: 'サービスを評価',
    back: '戻る',
    thankYou: 'ありがとうございます！',
    ratingSubmittedSuccessfully: '評価が正常に送信されました。',

    // Welcome Overlay
    welcome: 'ようこそ！',
    meetYourAIWaiter: 'AIウェイターをご紹介 - メニューの探索とご注文をお手伝いします',
    tapToStart: 'タップして開始',
    tapAnywhereToContinue: 'どこでもタップして続ける',

    // Contact & Support
    contactSupport: 'サポートに連絡',
    questionsContact: '質問がありますか？連絡先',
    directly: '直接。',
    thankYouForDining: 'ご来店ありがとうございました！',
  },
  ar: {
    // Navigation
    menu: 'القائمة',
    cart: 'السلة',
    orders: 'الطلبات',
    settings: 'الإعدادات',

    // Menu Categories
    allCategories: 'جميع الفئات',
    appetizers: 'المقبلات',
    mainCourse: 'الطبق الرئيسي',
    desserts: 'الحلويات',
    beverages: 'المشروبات',

    // Menu Item Details
    spiceLevel: 'مستوى التوابل',
    vegan: 'نباتي',
    vegetarian: 'نباتي',
    halal: 'حلال',
    allergens: 'مسببات الحساسية',
    addToCart: 'أضف إلى السلة',

    // Cart
    yourCart: 'سلتك',
    emptyCart: 'سلتك فارغة',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    total: 'المجموع',
    checkout: 'الدفع',
    remove: 'إزالة',

    // Checkout
    paymentMethod: 'طريقة الدفع',
    payOnline: 'الدفع عبر الإنترنت',
    payWithCash: 'الدفع عند الصندوق (الدفع لاحقاً)',
    card: 'بطاقة',
    cash: 'الدفع لاحقاً',
    customerNotes: 'ملاحظات العميل',
    optional: 'اختياري',
    placeOrder: 'تقديم الطلب',
    processing: 'جاري المعالجة...',
    orderSuccess: 'تم تقديم الطلب بنجاح!',
    orderError: 'فشل تقديم الطلب',

    // AI Waiter
    aiWaiter: 'النادل الذكي',
    aiWaiterWelcome: 'مرحباً! أنا النادل الذكي الخاص بك. يمكنني مساعدتك في توصيات القائمة والقيود الغذائية ومسببات الحساسية والإجابة على أي أسئلة حول أطباقنا. كيف يمكنني مساعدتك اليوم؟',
    typeMessage: 'اكتب رسالتك...',
    send: 'إرسال',
    voiceToggle: 'تبديل الردود الصوتية',
    voiceInput: 'إدخال صوتي',
    close: 'إغلاق',
    tapToTalk: 'انقر للتحدث',
    listening: 'الاستماع...',
    aiProcessing: 'معالجة...',
    speaking: 'التحدث...',

    // Restaurant Info
    address: 'العنوان',
    phone: 'الهاتف',
    hours: 'ساعات العمل',

    // Status
    available: 'متاح',
    unavailable: 'غير متاح',
    loading: 'جاري التحميل...',
    error: 'خطأ',

    // Common
    cancel: 'إلغاء',
    save: 'حفظ',
    delete: 'حذف',
    edit: 'تعديل',
    confirm: 'تأكيد',
    yes: 'نعم',
    no: 'لا',

    // Call Waiter
    callForAssistance: 'طلب المساعدة',
    callWaiter: 'استدعاء النادل',
    requestSent: 'تم إرسال الطلب!',
    someoneWillBeWithYouSoon: 'سيكون شخص ما معك قريباً',
    assistanceQuestion: 'هل هناك شيء محدد يمكننا مساعدتك به؟ (اختياري)',
    assistancePlaceholder: 'مثل، أحتاج مناديل إضافية، لدي سؤال حول القائمة، إلخ.',
    staffNotified: 'سيتم إخطار أحد الموظفين وسيأتي إلى طاولتك قريباً.',
    waiterCalled: 'تم استدعاء النادل',
    staffMemberWillBeWithYouShortly: 'سيكون أحد الموظفين معك قريباً!',
    unableToContactRestaurant: 'غير قادر على الاتصال بالمطعم. يرجى تحديث الصفحة.',

    // Receipt
    emailReceipt: 'إرسال الإيصال عبر البريد الإلكتروني',
    receipt: 'إيصال',
    invoice: 'فاتورة',
    thankYouForYourOrder: 'شكراً لطلبك',
    orderNumber: 'رقم الطلب',
    dateAndTime: 'التاريخ والوقت',
    table: 'طاولة',
    orderItems: 'عناصر الطلب',
    receiptSent: 'تم إرسال الإيصال!',
    receiptSentTo: 'تم إرسال إيصالك إلى',
    failedToSend: 'فشل الإرسال',
    couldNotSendEmail: 'تعذر إرسال الإيصال عبر البريد الإلكتروني. يرجى المحاولة مرة أخرى.',
    invalidEmail: 'بريد إلكتروني غير صالح',
    pleaseEnterValidEmail: 'يرجى إدخال عنوان بريد إلكتروني صالح.',
    emailAddress: 'عنوان البريد الإلكتروني',
    sendReceipt: 'إرسال الإيصال',
    sending: 'جاري الإرسال...',
    printSavePDF: 'طباعة / حفظ PDF',
    printing: 'جاري الطباعة...',
    trackYourOrder: 'تتبع طلبك',
    trackOrder: 'تتبع الطلب',
    continueBrowsingMenu: 'متابعة تصفح القائمة',
    specialInstructions: 'تعليمات خاصة',
    paid: 'مدفوع',
    pending: 'قيد الانتظار',
    paymentCompleted: 'اكتمل الدفع',
    paymentPending: 'الدفع قيد الانتظار',
    receiptNotFound: 'لم يتم العثور على الإيصال',
    openingOrderTracking: 'فتح تتبع الطلب',
    orderTrackingWillOpen: 'ستفتح صفحة تتبع طلبك في علامة تبويب جديدة',
    enterEmailToReceiveReceipt: 'أدخل عنوان بريدك الإلكتروني لتلقي نسخة من هذا الإيصال',

    // Rating & Feedback
    submitRating: 'إرسال التقييم',
    foodQuality: 'جودة الطعام',
    serviceSpeed: 'سرعة الخدمة',
    cleanliness: 'النظافة',
    staffFriendliness: 'ودود الموظفين',
    howWasThisDish: 'كيف كان هذا الطبق؟',
    overallExperience: 'التجربة الشاملة',
    additionalFeedback: 'أي ملاحظات إضافية؟',
    rateYourFood: 'قيّم طعامك',
    rateOurService: 'قيّم خدمتنا',
    rateItem: 'تقييم العنصر',
    of: 'من',
    tellUsAboutYourExperience: 'أخبرنا عن تجربتك الشاملة',
    additionalComments: 'تعليقات إضافية (اختياري)',
    tellUsMoreAboutYourExperience: 'أخبرنا المزيد عن تجربتك...',
    previous: 'السابق',
    nextItem: 'العنصر التالي',
    rateService: 'تقييم الخدمة',
    back: 'رجوع',
    thankYou: 'شكراً لك!',
    ratingSubmittedSuccessfully: 'تم إرسال تقييمك بنجاح.',

    // Welcome Overlay
    welcome: 'مرحباً!',
    meetYourAIWaiter: 'قابل النادل الذكي الخاص بك - جاهز لمساعدتك في استكشاف قائمتنا وتقديم طلبك',
    tapToStart: 'اضغط للبدء',
    tapAnywhereToContinue: 'اضغط في أي مكان للمتابعة',

    // Contact & Support
    contactSupport: 'الاتصال بالدعم',
    questionsContact: 'أسئلة؟ اتصل بـ',
    directly: 'مباشرة.',
    thankYouForDining: 'شكراً لتناولك الطعام معنا!',
  },
  pt: {
    // Navigation
    menu: 'Menu',
    cart: 'Carrinho',
    orders: 'Pedidos',
    settings: 'Configurações',

    // Menu Categories
    allCategories: 'Todas as Categorias',
    appetizers: 'Entradas',
    mainCourse: 'Prato Principal',
    desserts: 'Sobremesas',
    beverages: 'Bebidas',

    // Menu Item Details
    spiceLevel: 'Nível de Picante',
    vegan: 'Vegano',
    vegetarian: 'Vegetariano',
    halal: 'Halal',
    allergens: 'Alérgenos',
    addToCart: 'Adicionar ao Carrinho',

    // Cart
    yourCart: 'Seu Carrinho',
    emptyCart: 'Seu carrinho está vazio',
    subtotal: 'Subtotal',
    tax: 'Taxa',
    total: 'Total',
    checkout: 'Finalizar Compra',
    remove: 'Remover',

    // Checkout
    paymentMethod: 'Método de Pagamento',
    payOnline: 'Pagar Online',
    payWithCash: 'Pagar no Balcão (Pagar Depois)',
    card: 'Cartão',
    cash: 'Pagar Depois',
    customerNotes: 'Notas do Cliente',
    optional: 'Opcional',
    placeOrder: 'Fazer Pedido',
    processing: 'Processando...',
    orderSuccess: 'Pedido realizado com sucesso!',
    orderError: 'Falha ao realizar pedido',

    // AI Waiter
    aiWaiter: 'Garçom IA',
    aiWaiterWelcome: 'Bem-vindo! Sou seu Garçom IA. Posso ajudá-lo com recomendações do menu, restrições alimentares, alérgenos e responder a qualquer pergunta sobre nossos pratos. Como posso ajudá-lo hoje?',
    typeMessage: 'Digite sua mensagem...',
    send: 'Enviar',
    voiceToggle: 'Alternar respostas de voz',
    voiceInput: 'Entrada de voz',
    close: 'Fechar',
    tapToTalk: 'Toque para falar',
    listening: 'Ouvindo...',
    aiProcessing: 'Processando...',
    speaking: 'Falando...',

    // Restaurant Info
    address: 'Endereço',
    phone: 'Telefone',
    hours: 'Horário',

    // Status
    available: 'Disponível',
    unavailable: 'Indisponível',
    loading: 'Carregando...',
    error: 'Erro',

    // Common
    cancel: 'Cancelar',
    save: 'Salvar',
    delete: 'Excluir',
    edit: 'Editar',
    confirm: 'Confirmar',
    yes: 'Sim',
    no: 'Não',

    // Call Waiter
    callForAssistance: 'Chamar por Assistência',
    callWaiter: 'Chamar Garçom',
    requestSent: 'Pedido Enviado!',
    someoneWillBeWithYouSoon: 'Alguém estará com você em breve',
    assistanceQuestion: 'Há algo específico em que possamos ajudá-lo? (Opcional)',
    assistancePlaceholder: 'Ex., Preciso de guardanapos extras, tenho uma pergunta sobre o menu, etc.',
    staffNotified: 'Um membro da equipe será notificado e virá à sua mesa em breve.',
    waiterCalled: 'Garçom Chamado',
    staffMemberWillBeWithYouShortly: 'Um membro da equipe estará com você em breve!',
    unableToContactRestaurant: 'Não é possível contatar o restaurante. Por favor, atualize a página.',

    // Receipt
    emailReceipt: 'Enviar Recibo por Email',
    receipt: 'Recibo',
    invoice: 'Fatura',
    thankYouForYourOrder: 'Obrigado pelo seu pedido',
    orderNumber: 'Número do Pedido',
    dateAndTime: 'Data e Hora',
    table: 'Mesa',
    orderItems: 'Itens do Pedido',
    receiptSent: 'Recibo Enviado!',
    receiptSentTo: 'Seu recibo foi enviado para',
    failedToSend: 'Falha ao Enviar',
    couldNotSendEmail: 'Não foi possível enviar o recibo por email. Por favor, tente novamente.',
    invalidEmail: 'Email Inválido',
    pleaseEnterValidEmail: 'Por favor, insira um endereço de email válido.',
    emailAddress: 'Endereço de Email',
    sendReceipt: 'Enviar Recibo',
    sending: 'Enviando...',
    printSavePDF: 'Imprimir / Salvar PDF',
    printing: 'Imprimindo...',
    trackYourOrder: 'Rastrear Seu Pedido',
    trackOrder: 'Rastrear Pedido',
    continueBrowsingMenu: 'Continuar Navegando no Menu',
    specialInstructions: 'Instruções Especiais',
    paid: 'Pago',
    pending: 'Pendente',
    paymentCompleted: 'Pagamento Concluído',
    paymentPending: 'Pagamento Pendente',
    receiptNotFound: 'Recibo não encontrado',
    openingOrderTracking: 'Abrindo Rastreamento do Pedido',
    orderTrackingWillOpen: 'Sua página de rastreamento do pedido abrirá em uma nova aba',
    enterEmailToReceiveReceipt: 'Digite seu endereço de email para receber uma cópia deste recibo',

    // Rating & Feedback
    submitRating: 'Enviar Avaliação',
    foodQuality: 'Qualidade da Comida',
    serviceSpeed: 'Velocidade do Serviço',
    cleanliness: 'Limpeza',
    staffFriendliness: 'Simpatia da Equipe',
    howWasThisDish: 'Como foi este prato?',
    overallExperience: 'Experiência Geral',
    additionalFeedback: 'Algum feedback adicional?',
    rateYourFood: 'Avalie Sua Comida',
    rateOurService: 'Avalie Nosso Serviço',
    rateItem: 'Avaliar item',
    of: 'de',
    tellUsAboutYourExperience: 'Conte-nos sobre sua experiência geral',
    additionalComments: 'Comentários Adicionais (Opcional)',
    tellUsMoreAboutYourExperience: 'Conte-nos mais sobre sua experiência...',
    previous: 'Anterior',
    nextItem: 'Próximo Item',
    rateService: 'Avaliar Serviço',
    back: 'Voltar',
    thankYou: 'Obrigado!',
    ratingSubmittedSuccessfully: 'Sua avaliação foi enviada com sucesso.',

    // Welcome Overlay
    welcome: 'Bem-vindo!',
    meetYourAIWaiter: 'Conheça seu garçom IA - pronto para ajudá-lo a explorar nosso menu e fazer seu pedido',
    tapToStart: 'Toque para Começar',
    tapAnywhereToContinue: 'Toque em qualquer lugar para continuar',

    // Contact & Support
    contactSupport: 'Contactar Suporte',
    questionsContact: 'Perguntas? Contacte',
    directly: 'diretamente.',
    thankYouForDining: 'Obrigado por jantar conosco!',
  },
  ru: {
    // Navigation
    menu: 'Меню',
    cart: 'Корзина',
    orders: 'Заказы',
    settings: 'Настройки',

    // Menu Categories
    allCategories: 'Все Категории',
    appetizers: 'Закуски',
    mainCourse: 'Основное Блюдо',
    desserts: 'Десерты',
    beverages: 'Напитки',

    // Menu Item Details
    spiceLevel: 'Уровень Остроты',
    vegan: 'Веган',
    vegetarian: 'Вегетарианец',
    halal: 'Халяль',
    allergens: 'Аллергены',
    addToCart: 'Добавить в Корзину',

    // Cart
    yourCart: 'Ваша Корзина',
    emptyCart: 'Ваша корзина пуста',
    subtotal: 'Промежуточный итог',
    tax: 'Налог',
    total: 'Итого',
    checkout: 'Оформить Заказ',
    remove: 'Удалить',

    // Checkout
    paymentMethod: 'Способ Оплаты',
    payOnline: 'Оплатить Онлайн',
    payWithCash: 'Оплата на Стойке (Оплатить Позже)',
    card: 'Карта',
    cash: 'Оплатить Позже',
    customerNotes: 'Заметки Клиента',
    optional: 'Необязательно',
    placeOrder: 'Разместить Заказ',
    processing: 'Обработка...',
    orderSuccess: 'Заказ успешно размещен!',
    orderError: 'Ошибка при размещении заказа',

    // AI Waiter
    aiWaiter: 'ИИ Официант',
    aiWaiterWelcome: 'Добро пожаловать! Я ваш ИИ-официант. Я могу помочь вам с рекомендациями меню, диетическими ограничениями, аллергенами и ответить на любые вопросы о наших блюдах. Как я могу помочь вам сегодня?',
    typeMessage: 'Введите ваше сообщение...',
    send: 'Отправить',
    voiceToggle: 'Переключить голосовые ответы',
    voiceInput: 'Голосовой ввод',
    close: 'Закрыть',
    tapToTalk: 'Нажмите, чтобы говорить',
    listening: 'Слушаю...',
    aiProcessing: 'Обработка...',
    speaking: 'Говорю...',

    // Restaurant Info
    address: 'Адрес',
    phone: 'Телефон',
    hours: 'Часы работы',

    // Status
    available: 'Доступно',
    unavailable: 'Недоступно',
    loading: 'Загрузка...',
    error: 'Ошибка',

    // Common
    cancel: 'Отменить',
    save: 'Сохранить',
    delete: 'Удалить',
    edit: 'Редактировать',
    confirm: 'Подтвердить',
    yes: 'Да',
    no: 'Нет',

    // Call Waiter
    callForAssistance: 'Вызвать Помощь',
    callWaiter: 'Вызвать Официанта',
    requestSent: 'Запрос Отправлен!',
    someoneWillBeWithYouSoon: 'Кто-то скоро будет с вами',
    assistanceQuestion: 'Есть ли что-то конкретное, с чем мы можем вам помочь? (Необязательно)',
    assistancePlaceholder: 'Например, нужны дополнительные салфетки, есть вопрос о меню и т.д.',
    staffNotified: 'Сотрудник будет уведомлен и скоро подойдет к вашему столику.',
    waiterCalled: 'Официант Вызван',
    staffMemberWillBeWithYouShortly: 'Сотрудник скоро будет с вами!',
    unableToContactRestaurant: 'Невозможно связаться с рестораном. Пожалуйста, обновите страницу.',

    // Receipt
    emailReceipt: 'Отправить Чек по Email',
    receipt: 'Чек',
    invoice: 'Счет',
    thankYouForYourOrder: 'Спасибо за ваш заказ',
    orderNumber: 'Номер Заказа',
    dateAndTime: 'Дата и Время',
    table: 'Стол',
    orderItems: 'Позиции Заказа',
    receiptSent: 'Чек Отправлен!',
    receiptSentTo: 'Ваш чек был отправлен на',
    failedToSend: 'Не Удалось Отправить',
    couldNotSendEmail: 'Не удалось отправить чек по электронной почте. Пожалуйста, попробуйте еще раз.',
    invalidEmail: 'Неверный Email',
    pleaseEnterValidEmail: 'Пожалуйста, введите действительный адрес электронной почты.',
    emailAddress: 'Адрес Электронной Почты',
    sendReceipt: 'Отправить Чек',
    sending: 'Отправка...',
    printSavePDF: 'Печать / Сохранить PDF',
    printing: 'Печать...',
    trackYourOrder: 'Отследить Ваш Заказ',
    trackOrder: 'Отследить Заказ',
    continueBrowsingMenu: 'Продолжить Просмотр Меню',
    specialInstructions: 'Особые Инструкции',
    paid: 'Оплачено',
    pending: 'В Ожидании',
    paymentCompleted: 'Оплата Завершена',
    paymentPending: 'Оплата в Ожидании',
    receiptNotFound: 'Чек не найден',
    openingOrderTracking: 'Открытие Отслеживания Заказа',
    orderTrackingWillOpen: 'Ваша страница отслеживания заказа откроется в новой вкладке',
    enterEmailToReceiveReceipt: 'Введите свой адрес электронной почты, чтобы получить копию этого чека',

    // Rating & Feedback
    submitRating: 'Отправить Оценку',
    foodQuality: 'Качество Еды',
    serviceSpeed: 'Скорость Обслуживания',
    cleanliness: 'Чистота',
    staffFriendliness: 'Доброжелательность Персонала',
    howWasThisDish: 'Как вам это блюдо?',
    overallExperience: 'Общее Впечатление',
    additionalFeedback: 'Дополнительные отзывы?',
    rateYourFood: 'Оцените Вашу Еду',
    rateOurService: 'Оцените Наш Сервис',
    rateItem: 'Оценить позицию',
    of: 'из',
    tellUsAboutYourExperience: 'Расскажите нам о вашем общем впечатлении',
    additionalComments: 'Дополнительные Комментарии (Необязательно)',
    tellUsMoreAboutYourExperience: 'Расскажите нам подробнее о вашем опыте...',
    previous: 'Предыдущий',
    nextItem: 'Следующая Позиция',
    rateService: 'Оценить Сервис',
    back: 'Назад',
    thankYou: 'Спасибо!',
    ratingSubmittedSuccessfully: 'Ваша оценка успешно отправлена.',

    // Welcome Overlay
    welcome: 'Добро пожаловать!',
    meetYourAIWaiter: 'Познакомьтесь с вашим ИИ-официантом - готовым помочь вам изучить наше меню и сделать заказ',
    tapToStart: 'Нажмите, чтобы Начать',
    tapAnywhereToContinue: 'Нажмите в любом месте, чтобы продолжить',

    // Contact & Support
    contactSupport: 'Связаться с Поддержкой',
    questionsContact: 'Вопросы? Свяжитесь с',
    directly: 'напрямую.',
    thankYouForDining: 'Спасибо, что поужинали с нами!',
  },
} as const;

export function getTranslation(lang: Language, key: TranslationKey): string {
  return translations[lang][key] || translations.en[key];
}
